/**
 * 5897 — Detalhe do evento: curva acumulada contra a faixa dos comparáveis,
 * projeção, alertas, comparáveis usados (com escore) e lotes do evento.
 */
import { FC, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { usePrevisaoEvento } from '../../../hooks/previsoes'
import { PainelPrevisoes, PrevisaoEventoDetalhe } from '../../../types/previsoes'
import { BD, Button, Card, Field, FiltersRow, Input, PageSubtitle, PageTitle, Spinner, Table } from '../styles'
import {
  BarraReferencia,
  COR,
  CartaoTabela,
  ComPainel,
  Entenda,
  Kpi,
  Kpis,
  Rolagem,
  Selo,
  SeloAlerta,
  brl,
  comRef,
  fmtInt,
  useReferencia,
} from './comum'
import { TabelaLotes } from './Lotes'

const Grade = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  & > * {
    min-width: 0;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const Legenda = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${BD.grayText};
  margin: 4px 0;
  i {
    display: inline-block;
    width: 14px;
    height: 3px;
    border-radius: 2px;
    margin-right: 6px;
    vertical-align: middle;
  }
  i.caixa {
    height: 10px;
  }
`

export const Dica = styled.div`
  position: absolute;
  pointer-events: none;
  background: #fff;
  border: 1px solid ${BD.grayBorder};
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  padding: 8px 10px;
  font-size: 12.5px;
  z-index: 5;
  min-width: 160px;
  top: 60px;
  .quando {
    color: ${BD.grayText};
    font-size: 11.5px;
  }
  b {
    font-variant-numeric: tabular-nums;
  }
`

const W = 640
const H = 300
const M = { l: 56, r: 18, t: 10, b: 30 }
const XMAX = 60

const Curva: FC<{ e: PrevisaoEventoDetalhe }> = ({ e }) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const caixaRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ k: number; left: number } | null>(null)
  const ymax = Math.max(e.p50 * 1.2, e.pagos * 1.1, Math.min(e.p90, e.p50 * 1.6), 1)
  const x = (k: number): number => M.l + ((W - M.l - M.r) * (XMAX - Math.min(k, XMAX))) / XMAX
  const y = (v: number): number => H - M.b - ((H - M.t - M.b) * Math.min(v, ymax)) / ymax
  const B = e.banda
  const ks = B
    ? Object.keys(B)
        .map(Number)
        .sort((a, b) => b - a)
    : []
  const S = e.serie.filter((p) => p[0] <= XMAX)
  const ultimo = S[S.length - 1]

  const mover = (ev: React.PointerEvent): void => {
    const svg = svgRef.current
    const caixa = caixaRef.current
    if (!svg || !caixa) return
    const bb = svg.getBoundingClientRect()
    const px = ((ev.clientX - bb.left) * W) / bb.width
    const k = Math.max(0, Math.min(XMAX, Math.round(XMAX - ((px - M.l) / (W - M.l - M.r)) * XMAX)))
    const left = Math.min(ev.clientX - caixa.getBoundingClientRect().left + 12, caixa.clientWidth - 180)
    setHover({ k, left })
  }
  const pontoHover = hover ? e.serie.filter((p) => p[0] >= hover.k).pop() : undefined

  return (
    <Card ref={caixaRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Curva de vendas × eventos comparáveis</h3>
        <span style={{ fontSize: 12, color: BD.grayText }}>acumulado de pagos por dias até o evento</span>
      </div>
      <Legenda>
        <span>
          <i style={{ background: COR.serie }} />
          Este evento
        </span>
        {B && (
          <span>
            <i className="caixa" style={{ background: COR.banda }} />
            Comparáveis (faixa 25–75%), na escala da projeção
          </span>
        )}
        <span>
          <i style={{ background: COR.projecao }} />
          Projeção
        </span>
      </Legenda>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Curva acumulada do evento"
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(ymax * t)} y2={y(ymax * t)} stroke={COR.grade} strokeDasharray="3 3" />
            <text x={M.l - 6} y={y(ymax * t) + 4} textAnchor="end" fontSize={11} fill={COR.eixo}>
              {fmtInt(ymax * t)}
            </text>
          </g>
        ))}
        {[60, 45, 30, 14, 7, 0].map((k) => (
          <text key={k} x={x(k)} y={H - 8} textAnchor="middle" fontSize={11} fill={COR.eixo}>
            {k ? `D-${k}` : 'D0'}
          </text>
        ))}
        {B && (
          <>
            <path
              d={
                'M' +
                ks
                  .map((k) => [x(k), y(B[k][2] * e.p50)])
                  .concat(
                    ks
                      .slice()
                      .reverse()
                      .map((k) => [x(k), y(B[k][0] * e.p50)]),
                  )
                  .map((p) => p.join(','))
                  .join('L') +
                'Z'
              }
              fill={COR.banda}
            />
            <path
              d={'M' + ks.map((k) => [x(k), y(B[k][1] * e.p50)].join(',')).join('L')}
              fill="none"
              stroke={COR.anterior}
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
          </>
        )}
        {S.length > 0 && (
          <>
            <path
              d={'M' + S.map((p) => [x(p[0]), y(p[1])].join(',')).join('L')}
              fill="none"
              stroke={COR.serie}
              strokeWidth={2.5}
            />
            <circle cx={x(ultimo[0])} cy={y(ultimo[1])} r={4} fill={COR.serie} />
            <line
              x1={x(ultimo[0])}
              y1={y(ultimo[1])}
              x2={x(0)}
              y2={y(e.p50)}
              stroke={COR.projecao}
              strokeWidth={2}
              strokeDasharray="5 4"
            />
          </>
        )}
        <line
          x1={x(0)}
          x2={x(0)}
          y1={y(e.p10)}
          y2={y(e.p90)}
          stroke={COR.projecao}
          strokeWidth={6}
          strokeLinecap="round"
          opacity={0.3}
        />
        <circle cx={x(0)} cy={y(e.p50)} r={5} fill={COR.projecao} />
        {hover && <line x1={x(hover.k)} x2={x(hover.k)} y1={M.t} y2={H - M.b} stroke={BD.grayText} strokeDasharray="2 3" />}
        <rect
          x={M.l}
          y={M.t}
          width={W - M.l - M.r}
          height={H - M.t - M.b}
          fill="transparent"
          onPointerMove={mover}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {hover && (
        <Dica style={{ left: hover.left }}>
          <div className="quando">{hover.k ? `D-${hover.k}` : 'Dia do evento'}</div>
          <div>
            Este evento: <b>{pontoHover ? fmtInt(pontoHover[1]) : '—'}</b>
          </div>
          {B && (
            <div>
              Comparáveis (mediana): <b>{B[hover.k] ? fmtInt(B[hover.k][1] * e.p50) : '—'}</b>
            </div>
          )}
        </Dica>
      )}
      <Entenda
        paragrafos={[
          'A linha azul é o acumulado de ingressos pagos deste evento até ontem. A faixa clara mostra onde os eventos comparáveis estavam, em cada dia antes do evento, na mesma escala da projeção final: se a linha azul está dentro da faixa, o evento segue o padrão de eventos parecidos.',
          'O ponto vermelho no dia do evento é a projeção (P50), com a faixa provável (P10–P90) desenhada como barra.',
          'Cuidado: a projeção é de público vendido. Se o último lote esgotar, a venda bate no teto e a demanda real fica desconhecida.',
        ]}
      />
    </Card>
  )
}

const Sugestao = styled.div`
  border: 1px solid ${BD.grayBorder};
  border-radius: 10px;
  padding: 12px 14px;
  p {
    margin: 6px 0 0;
    color: ${BD.grayText};
    font-size: 13px;
  }
`

const Conteudo: FC<{ painel: PainelPrevisoes; referencias: string[] }> = ({ painel, referencias }) => {
  const navigate = useNavigate()
  const [ref] = useReferencia()
  const { id } = useParams()
  const lista = painel.eventos
  const atual = lista.find((x) => x.id === Number(id))
  // o detalhe vem do MESMO snapshot do resumo, mesmo que o cron publique outro no meio
  const { detalhe, isLoading } = usePrevisaoEvento(atual?.id, painel.referencia)
  const [busca, setBusca] = useState('')

  if (!atual)
    return (
      <>
        <PageTitle>Evento não encontrado</PageTitle>
        <PageSubtitle>
          O evento {id} não está na carteira desta previsão (já aconteceu, tem menos de 20 pagos ou está fora dos
          próximos 120 dias).
        </PageSubtitle>
        <Button type="button" $variant="secondary" onClick={() => navigate(comRef('/interno/previsoes', ref))}>
          Voltar à carteira
        </Button>
      </>
    )
  const idx = lista.indexOf(atual)
  const rotulo = (x: typeof atual): string => `${x.nome} · ${x.data} · ${x.cidade}`
  const ir = (eventoId: number): void => {
    setBusca('')
    navigate(comRef(`/interno/previsoes/evento/${eventoId}`, ref))
    window.scrollTo(0, 0)
  }
  const trocar = (): void => {
    const t = busca.trim().toLowerCase()
    if (!t) return
    const x = lista.find((z) => rotulo(z) === busca) ?? lista.find((z) => z.nome.toLowerCase().includes(t))
    if (x) ir(x.id)
  }
  const passo = (d: number): void => ir(lista[(idx + d + lista.length) % lista.length].id)
  const e = detalhe?.evento

  return (
    <>
      <FiltersRow
        as="form"
        onSubmit={(ev) => {
          ev.preventDefault()
          trocar()
        }}
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <Field style={{ flex: '1 1 320px' }}>
          <label htmlFor="pv-ev">Trocar evento ({lista.length} na carteira)</label>
          <Input
            id="pv-ev"
            type="search"
            list="pv-ev-lista"
            placeholder="Digite o nome do evento…"
            value={busca}
            onChange={(ev) => {
              setBusca(ev.target.value)
              const x = lista.find((z) => rotulo(z) === ev.target.value)
              if (x) ir(x.id)
            }}
          />
          <datalist id="pv-ev-lista">
            {lista.map((x) => (
              <option key={x.id} value={rotulo(x)} />
            ))}
          </datalist>
        </Field>
        <Button type="submit">Abrir</Button>
        <Button type="button" $variant="secondary" onClick={() => passo(-1)}>
          ‹ Anterior
        </Button>
        <Button type="button" $variant="secondary" onClick={() => passo(1)}>
          Próximo ›
        </Button>
        <Button type="button" $variant="secondary" onClick={() => navigate(comRef('/interno/previsoes', ref))}>
          Voltar à carteira
        </Button>
      </FiltersRow>

      <PageTitle>{atual.nome}</PageTitle>
      <PageSubtitle>
        {atual.cidade}
        {atual.local ? ` · ${atual.local}` : ''} · {atual.estilo} · {atual.data_completa} (faltam {atual.dias} dias) ·
        produtor com {atual.hist} eventos anteriores no histórico
      </PageSubtitle>
      <BarraReferencia painel={painel} referencias={referencias} />
      <Kpis>
        <Kpi rotulo="Pagos até agora" valor={fmtInt(atual.pagos)} dica={`${brl(atual.gmv)} de GMV`} />
        <Kpi rotulo="Ritmo 7 dias" valor={fmtInt(atual.r7)} dica={`semana anterior: ${fmtInt(atual.r7a)}`} />
        <Kpi
          rotulo="Projeção final"
          valor={fmtInt(atual.p50)}
          dica={`faixa 80%: ${fmtInt(atual.p10)} – ${fmtInt(atual.p90)}`}
        />
        <Kpi
          rotulo="Confiança"
          valor={atual.confianca[0].toUpperCase() + atual.confianca.slice(1)}
          dica={atual.longo ? '31–120 dias do evento' : 'até 30 dias do evento'}
        />
      </Kpis>

      {isLoading || !e ? (
        <Card style={{ padding: 48 }}>
          {isLoading ? <Spinner /> : <PageSubtitle>Detalhe do evento indisponível.</PageSubtitle>}
        </Card>
      ) : (
        <>
          <Grade>
            <Curva e={e} />
            <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
              <Card>
                <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>Alertas</h3>
                <div style={{ display: 'grid', gap: 10 }}>
                  {e.alertas.length > 0 ? (
                    e.alertas.map((a) => (
                      <Sugestao key={a}>
                        <SeloAlerta alerta={a} />
                        <Selo tom="neutro">alerta descritivo</Selo>
                        <p>
                          {a.includes('expectativa')
                            ? `Projeção de 7 dias atrás: ${fmtInt(e.p50_7d_atras ?? 0)}; hoje: ${fmtInt(e.p50)}. Não indica a causa.`
                            : 'Comparado ao ritmo da semana anterior. Não indica a causa.'}
                        </p>
                      </Sugestao>
                    ))
                  ) : (
                    <Sugestao>
                      <Selo tom="bom">sem alertas</Selo>
                      <p>Projeção estável e ritmo semelhante à semana anterior.</p>
                    </Sugestao>
                  )}
                </div>
              </Card>
              <CartaoTabela>
                <div className="titulo">
                  <h3>Comparáveis usados</h3>
                  <div style={{ fontSize: 12, color: BD.grayText }}>
                    escore transparente (produtor, local, cidade, estilo, UF, preço, volume, dia) · qualidade:{' '}
                    {e.comparaveis_qualidade}
                  </div>
                </div>
                {e.comparaveis.length > 0 ? (
                  <Rolagem>
                    <Table>
                      <thead>
                        <tr>
                          <th>Evento</th>
                          <th className="n">Escore</th>
                          <th className="n">Neste ponto</th>
                          <th className="n">Final</th>
                        </tr>
                      </thead>
                      <tbody>
                        {e.comparaveis.map((c) => (
                          <tr key={c.id} style={{ cursor: 'default' }}>
                            <td style={{ fontWeight: 500 }}>
                              {c.nome}
                              <small>
                                {c.cidade} · {c.data}
                                {c.edicao_anterior ? ' · edição anterior' : ''}
                              </small>
                            </td>
                            <td className="n">{String(c.escore).replace('.', ',')}</td>
                            <td className="n">{fmtInt(c.vendido_mesmo_ponto)}</td>
                            <td className="n">{fmtInt(c.pagos)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Rolagem>
                ) : (
                  <p style={{ padding: '0 16px 16px', color: BD.grayText }}>Comparáveis insuficientes.</p>
                )}
              </CartaoTabela>
            </div>
          </Grade>
          <CartaoTabela style={{ marginTop: 16 }}>
            <div className="titulo">
              <h3>Lotes deste evento</h3>
            </div>
            {detalhe && detalhe.lotes.length > 0 ? (
              <TabelaLotes lotes={detalhe.lotes} comEvento={false} />
            ) : (
              <p style={{ padding: '0 16px 16px', color: BD.grayText }}>
                Nenhum lote aberto com quantidade definida neste evento (lotes sem limite não têm previsão de
                esgotamento).
              </p>
            )}
          </CartaoTabela>
        </>
      )}
    </>
  )
}

export const PrevisoesEvento: FC = () => (
  <ComPainel>{(painel, referencias) => <Conteudo painel={painel} referencias={referencias} />}</ComPainel>
)
